import { Coins, Info, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * DiploksContextHint
 * ----
 * A small, read-only informational chip surfaced on dashboards of roles that
 * don't own the organisation Diploks budget but should still understand
 * which premium actions consume organiser Diploks. NEVER renders a top-up
 * CTA — those roles cannot purchase Diploks.
 *
 * Default surfaces on: chair, faculty_advisor, parent.
 * Caller can pass a `context` prop to localise the wording.
 */
export default function DiploksContextHint({
  context = "premium reports",
  testId = "diploks-context-hint",
}) {
  const { user } = useAuth();
  // Hide for roles that already see the wallet
  const wallet_roles = ["super_admin", "org_admin", "event_manager", "school_admin"];
  if (!user || wallet_roles.includes(user.role)) return null;

  // Hide for roles that should not even be aware of Diploks at all.
  const hidden_roles = ["sponsor", "judge", "partner_institution", "volunteer"];
  if (hidden_roles.includes(user.role)) return null;

  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-3"
    >
      <div className="h-8 w-8 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
        <Coins className="h-4 w-4 text-[#0A192F]" />
      </div>
      <div className="flex-1 min-w-0 text-xs text-slate-700 leading-relaxed">
        <p className="font-semibold text-[#0A192F]">
          {context.charAt(0).toUpperCase() + context.slice(1)} use organiser Diploks
        </p>
        <p className="mt-0.5">
          This premium action consumes Diplomexa platform credits held by your
          event organiser. If the action is unavailable, please contact your
          organiser to top up.
        </p>
        <Link
          to="/policies/billing-notice"
          className="mt-1.5 inline-flex items-center gap-1 text-[#0F4C81] hover:underline"
        >
          <Info className="h-3 w-3" /> About Diploks
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
